// Colour contrast between two tokens of the theme (tokens.css), for the
// test that guards the theme's levels (tokens.test.ts).

export type Rgb = readonly [number, number, number];

/** A `#rgb` or `#rrggbb` colour's channels, 0–255. Throws on anything else (an alpha channel included: contrast needs opaque colours). */
export function parseHex(hex: string): Rgb {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) throw new Error(`not an opaque hex colour: ${hex}`);
  const h = m[1].length === 3 ? [...m[1]].map((c) => c + c).join('') : m[1];
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as unknown as Rgb;
}

/** WCAG 2 relative luminance, 0 (black) to 1 (white). */
export function luminance(hex: string): number {
  const [r, g, b] = parseHex(hex).map((c) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2 contrast ratio of two colours, 1 to 21, whichever is lighter. */
export function wcag(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * APCA lightness contrast (Lc) of text on a background, as its magnitude:
 * 0 to about 108. APCA-W3 0.0.98G-4g. Unlike the WCAG 2 ratio it follows
 * perceived contrast on dark backgrounds, where the ratio overstates it.
 */
export function apca(text: string, background: string): number {
  const y = (hex: string) => {
    const [r, g, b] = parseHex(hex).map((c) => (c / 255) ** 2.4);
    const lum = 0.2126729 * r + 0.7151522 * g + 0.072175 * b;
    return lum > 0.022 ? lum : lum + (0.022 - lum) ** 1.414;
  };
  const [txt, bg] = [y(text), y(background)];
  if (Math.abs(bg - txt) < 0.0005) return 0;
  const sapc = bg > txt ? (bg ** 0.56 - txt ** 0.57) * 1.14 : (bg ** 0.65 - txt ** 0.62) * 1.14;
  return Math.abs(sapc) < 0.1 ? 0 : (Math.abs(sapc) - 0.027) * 100;
}

/** The custom properties a CSS rule body declares (`--name:value`), by name without the dashes. */
function declarations(body: string): Record<string, string> {
  return Object.fromEntries([...body.matchAll(/--([\w-]+)\s*:\s*([^;}]+)/g)].map((m) => [m[1], m[2].trim()]));
}

/** The body of the first rule in `css` whose selector is exactly `selector`. */
function ruleBody(css: string, selector: string): string {
  const at = css.indexOf(`${selector}{`);
  if (at < 0) throw new Error(`no rule for ${selector}`);
  const start = at + selector.length + 1;
  return css.slice(start, css.indexOf('}', start));
}

export type Tokens = Record<string, string>;

/**
 * The theme's tokens as tokens.css declares them: `light` from `:root`,
 * `dark` (light's tokens with dark's over them) from the explicit
 * `:root[data-theme="dark"]` rule, and `systemDark` from the
 * `prefers-color-scheme` rule, which must say the same as `dark`.
 */
export function themes(css: string): { light: Tokens; dark: Tokens; systemDark: Tokens } {
  const light = declarations(ruleBody(css, ':root'));
  return {
    light,
    dark: { ...light, ...declarations(ruleBody(css, ':root[data-theme="dark"]')) },
    systemDark: { ...light, ...declarations(ruleBody(css, ':root:not([data-theme="light"])')) },
  };
}
